import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAIModel } from '../contexts/AIModelContext';
import ModelSelector from './ModelSelector';
import axios from 'axios';
import {
  Swords, Flame, TrendingUp, Stethoscope, GraduationCap, Clock,
  Shield, Laugh, Send, Loader2, RotateCcw, ChevronLeft, Briefcase,
  MessageSquare, Sparkles, Brain, X, User, Bot
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERSONAS = [
  {
    id: 'drill_sergeant',
    name: 'The Drill Sergeant',
    tagline: 'Zero tolerance for bad trades',
    icon: Swords,
    color: 'from-red-600 to-red-800',
    accent: 'text-red-400',
    bg: 'bg-red-500/10 border-red-500/30 hover:bg-red-500/20',
    preview: '"LISTEN UP! You bought weeklies on earnings?! Drop and give me 20 reasons why that was stupid!"',
  },
  {
    id: 'gordon_ramsay',
    name: 'Gordon Ramsay of Trading',
    tagline: 'Passionate, explosive, wants you to succeed',
    icon: Flame,
    color: 'from-orange-600 to-red-600',
    accent: 'text-orange-400',
    bg: 'bg-orange-500/10 border-orange-500/30 hover:bg-orange-500/20',
    preview: '"This portfolio is RAWWW! Where\'s the stop loss?! It\'s DISGUSTING!"',
  },
  {
    id: 'wall_street_wolf',
    name: 'The Wall Street Wolf',
    tagline: 'Aggressive, high-energy, profit-obsessed',
    icon: TrendingUp,
    color: 'from-green-600 to-emerald-700',
    accent: 'text-green-400',
    bg: 'bg-green-500/10 border-green-500/30 hover:bg-green-500/20',
    preview: '"The market doesn\'t care about your feelings. Think like a wolf, not a sheep."',
  },
  {
    id: 'surgeon',
    name: 'The Surgeon',
    tagline: 'Precise, clinical, cuts through the BS',
    icon: Stethoscope,
    color: 'from-cyan-600 to-blue-700',
    accent: 'text-cyan-400',
    bg: 'bg-cyan-500/10 border-cyan-500/30 hover:bg-cyan-500/20',
    preview: '"Your win rate is 32%. That\'s not a strategy. That\'s a donation to market makers."',
  },
  {
    id: 'professor',
    name: 'The Professor',
    tagline: 'Intellectually brutal, makes you smarter',
    icon: GraduationCap,
    color: 'from-indigo-600 to-purple-700',
    accent: 'text-indigo-400',
    bg: 'bg-indigo-500/10 border-indigo-500/30 hover:bg-indigo-500/20',
    preview: '"If you understood basic probability, you wouldn\'t have entered this trade. Elementary."',
  },
  {
    id: 'old_school',
    name: 'The Old-School Mentor',
    tagline: 'Tough but wise, survived every crash',
    icon: Clock,
    color: 'from-amber-600 to-yellow-700',
    accent: 'text-amber-400',
    bg: 'bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20',
    preview: '"Kid, I lost my shirt in \'08 so you wouldn\'t have to. Stop repeating my mistakes."',
  },
  {
    id: 'spartan_coach',
    name: 'The Spartan Coach',
    tagline: 'Discipline is profit, emotion is the enemy',
    icon: Shield,
    color: 'from-slate-600 to-zinc-700',
    accent: 'text-slate-300',
    bg: 'bg-slate-500/10 border-slate-500/30 hover:bg-slate-500/20',
    preview: '"You dishonored your own trading plan. That is worse than any loss."',
  },
  {
    id: 'roast_master',
    name: 'The Roast Master',
    tagline: 'Savage humor with real trading gems',
    icon: Laugh,
    color: 'from-pink-600 to-purple-600',
    accent: 'text-pink-400',
    bg: 'bg-pink-500/10 border-pink-500/30 hover:bg-pink-500/20',
    preview: '"Your portfolio looks like you let your cat walk across the keyboard."',
  },
];

const STARTER_PROMPTS = [
  "Analyze my trading approach - I mostly buy weekly options",
  "I just lost 30% on a single trade. What now?",
  "How should I manage risk with a $10,000 account?",
  "Is selling covered calls actually worth it?",
  "What's your take on iron condors in this market?",
  "I'm thinking about going all-in on NVDA calls",
];

const RuthlessMentor = ({ portfolioPositions = [] }) => {
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const { model: aiModel, setModel: setAiModel } = useAIModel();
  const [analyzingPortfolio, setAnalyzingPortfolio] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const selectPersona = (persona) => {
    setSelectedPersona(persona);
    setMessages([]);
    setSessionId(null);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const goBack = () => {
    setSelectedPersona(null);
    setMessages([]);
    setSessionId(null);
  };

  const sendMessage = async (text) => {
    if (!text.trim() || !selectedPersona || loading) return;

    const userMsg = { role: 'user', content: text, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/mentor/chat`, {
        persona_id: selectedPersona.id,
        message: text,
        session_id: sessionId,
        ai_model: aiModel,
      }, { timeout: 60000 });

      if (!sessionId) setSessionId(res.data.session_id);

      const botMsg = {
        role: 'assistant',
        content: res.data.response,
        timestamp: res.data.timestamp,
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (err) {
      console.error('Chat error:', err);
      const errorContent = err.response?.data?.detail?.includes?.('Budget')
        ? 'LLM budget exceeded. Please add balance at Profile > Universal Key > Add Balance.'
        : 'Failed to get response. Please try again.';
      toast.error(errorContent);
      setMessages(prev => [...prev, { role: 'error', content: errorContent }]);
    } finally {
      setLoading(false);
    }
  };

  const analyzePortfolio = async () => {
    if (!selectedPersona || analyzingPortfolio) return;
    if (!portfolioPositions || portfolioPositions.length === 0) {
      toast.error('No portfolio positions available. Import positions first.');
      return;
    }

    setAnalyzingPortfolio(true);
    const userMsg = {
      role: 'user',
      content: `[Analyze my portfolio - ${portfolioPositions.length} positions]`,
      timestamp: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await axios.post(`${API}/mentor/analyze-portfolio`, {
        persona_id: selectedPersona.id,
        positions: portfolioPositions,
        ai_model: aiModel,
      }, { timeout: 90000 });

      if (!sessionId) setSessionId(res.data.session_id);

      const botMsg = {
        role: 'assistant',
        content: res.data.analysis,
        timestamp: res.data.timestamp,
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (err) {
      console.error('Portfolio analysis error:', err);
      toast.error('Failed to analyze portfolio');
      setMessages(prev => [...prev, { role: 'error', content: 'Portfolio analysis failed.' }]);
    } finally {
      setAnalyzingPortfolio(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  // Persona Selection View
  if (!selectedPersona) {
    return (
      <div className="space-y-6" data-testid="mentor-persona-select">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-2xl flex items-center gap-3">
                  <div className="p-2.5 bg-red-500/20 rounded-xl border border-red-500/30">
                    <Swords className="h-6 w-6 text-red-400" />
                  </div>
                  Ruthless Mentor
                </CardTitle>
                <p className="text-slate-400 mt-2 text-sm max-w-lg">
                  Choose your AI trading mentor. Each has a unique personality that shapes their advice. 
                  They can analyze your portfolio, answer questions, and push you to trade better.
                </p>
              </div>
              <ModelSelector />
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PERSONAS.map((persona) => {
            const Icon = persona.icon;
            return (
              <button
                key={persona.id}
                onClick={() => selectPersona(persona)}
                className={`group text-left p-5 rounded-xl border transition-all duration-200 ${persona.bg}`}
                data-testid={`persona-${persona.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${persona.color} shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-white group-hover:text-white/90">{persona.name}</h3>
                    <p className="text-xs text-slate-400 mt-0.5">{persona.tagline}</p>
                    <p className="text-xs text-slate-500 mt-2 italic line-clamp-2">{persona.preview}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Chat View
  const Icon = selectedPersona.icon;
  return (
    <div className="space-y-0" data-testid="mentor-chat-view">
      {/* Chat Header */}
      <Card className={`border-0 shadow-lg bg-gradient-to-r ${selectedPersona.color} text-white rounded-b-none`}>
        <CardHeader className="py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" className="text-white/80 hover:text-white hover:bg-white/10"
                onClick={goBack} data-testid="mentor-back-btn">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <div className="p-2 bg-white/20 rounded-lg">
                <Icon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-base">{selectedPersona.name}</h3>
                <p className="text-xs text-white/70">{selectedPersona.tagline}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {portfolioPositions.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="border-white/30 text-white hover:bg-white/20 text-xs"
                  onClick={analyzePortfolio}
                  disabled={analyzingPortfolio}
                  data-testid="analyze-portfolio-btn"
                >
                  {analyzingPortfolio
                    ? <><Loader2 className="h-3 w-3 mr-1.5 animate-spin" /> Analyzing...</>
                    : <><Briefcase className="h-3 w-3 mr-1.5" /> Analyze Portfolio ({portfolioPositions.length})</>
                  }
                </Button>
              )}
              <Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10"
                onClick={() => { setMessages([]); setSessionId(null); }}
                data-testid="clear-chat-btn"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Messages Area */}
      <div className="bg-slate-900 border-x border-slate-700 min-h-[400px] max-h-[500px] overflow-y-auto p-4 space-y-4"
        data-testid="mentor-messages">
        {messages.length === 0 && (
          <div className="text-center py-8 space-y-4">
            <div className={`p-4 rounded-2xl bg-gradient-to-br ${selectedPersona.color} inline-block shadow-lg`}>
              <Icon className="h-8 w-8 text-white" />
            </div>
            <p className="text-slate-400 text-sm">Start a conversation with {selectedPersona.name}</p>
            <div className="flex flex-wrap justify-center gap-2 max-w-lg mx-auto">
              {STARTER_PROMPTS.slice(0, 4).map((prompt, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(prompt)}
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-700 text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                  data-testid={`starter-${i}`}
                >
                  {prompt}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role !== 'user' && (
              <div className={`shrink-0 p-1.5 rounded-lg h-8 w-8 flex items-center justify-center ${
                msg.role === 'error' ? 'bg-red-500/20' : `bg-gradient-to-br ${selectedPersona.color}`
              }`}>
                {msg.role === 'error' ? <X className="h-4 w-4 text-red-400" /> : <Icon className="h-4 w-4 text-white" />}
              </div>
            )}
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
              msg.role === 'user'
                ? 'bg-indigo-600 text-white rounded-br-sm'
                : msg.role === 'error'
                  ? 'bg-red-900/40 text-red-300 border border-red-800 rounded-bl-sm'
                  : 'bg-slate-800 text-slate-200 border border-slate-700 rounded-bl-sm'
            }`}>
              {msg.role === 'user' ? (
                <p>{msg.content}</p>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none [&>p]:mb-2 [&>ul]:mb-2 [&>ol]:mb-2 [&>h1]:text-base [&>h2]:text-sm [&>h3]:text-sm">
                  <ReactMarkdown>{msg.content}</ReactMarkdown>
                </div>
              )}
            </div>
            {msg.role === 'user' && (
              <div className="shrink-0 p-1.5 rounded-lg h-8 w-8 flex items-center justify-center bg-indigo-500/20">
                <User className="h-4 w-4 text-indigo-400" />
              </div>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex gap-3">
            <div className={`shrink-0 p-1.5 rounded-lg h-8 w-8 flex items-center justify-center bg-gradient-to-br ${selectedPersona.color}`}>
              <Icon className="h-4 w-4 text-white" />
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{selectedPersona.name} is typing...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-slate-900 border border-slate-700 rounded-b-xl p-3">
        <div className="flex items-center gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`Ask ${selectedPersona.name} anything about trading...`}
            className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-slate-500 min-h-[42px] max-h-[120px]"
            rows={1}
            disabled={loading}
            data-testid="mentor-input"
          />
          <Button
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading}
            className={`bg-gradient-to-r ${selectedPersona.color} hover:opacity-90 text-white h-[42px] px-4`}
            data-testid="mentor-send-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RuthlessMentor;
